import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCurrentUserName } from "@sd/hooks/useCurrentUserName";
import {
  appendTicketNoteToCache,
  isTicketNotesNotFoundError,
  parseCreatedTicketNote,
  type TicketNoteEntry,
} from "@sd/lib/ticketNotesApi";
import ServiceDeskAPIService, {
  serviceDeskTicketHistoryQueryKey,
  serviceDeskTicketNotesQueryKey,
} from "@sd/services/APIs";

function isUnauthorizedError(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 401;
}

export function TicketDetailAddNoteSection({
  ticketId,
}: {
  ticketId: string;
}) {
  const queryClient = useQueryClient();
  const { displayName } = useCurrentUserName();
  const [draftNote, setDraftNote] = useState("");

  const clearDraft = () => {
    setDraftNote("");
  };

  const addNoteMutation = useMutation({
    mutationFn: async (note: string) => {
      const res = await ServiceDeskAPIService.ticketNotes.create(
        { ticket_id: ticketId, note },
        { skipErrorNotification: true }
      );
      return { data: res.data, noteText: note };
    },
    onSuccess: async ({ data, noteText }) => {
      clearDraft();

      const created =
        parseCreatedTicketNote(data) ??
        ({
          id: `note-${Date.now()}`,
          note: noteText,
          createdOn: new Date().toISOString(),
          createdBy: displayName ?? "You",
        } satisfies TicketNoteEntry);

      const notesKey = serviceDeskTicketNotesQueryKey(ticketId);
      queryClient.setQueryData(notesKey, (old: unknown) =>
        appendTicketNoteToCache(old, created)
      );

      toast.success("Comment added");

      void Promise.all([
        queryClient.invalidateQueries({ queryKey: notesKey }),
        queryClient.invalidateQueries({
          queryKey: serviceDeskTicketHistoryQueryKey(ticketId),
        }),
      ]);
    },
    onError: (err: unknown) => {
      if (isTicketNotesNotFoundError(err)) {
        toast.error(
          "Comments API is not available on this server yet. Ask backend to deploy /service_desk/ticket_notes/."
        );
        return;
      }
      if (isUnauthorizedError(err)) {
        toast.error("Session expired. Please log in again and retry.");
        return;
      }
      toast.error("Could not add comment. Please try again.");
    },
  });

  const handleSubmit = async () => {
    const text = draftNote.trim();
    if (!text || addNoteMutation.isPending) return;
    try {
      await addNoteMutation.mutateAsync(text);
    } catch {
      // onError handles toasts
    }
  };

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border/80 bg-muted/30 px-4 py-2">
        <h3 className="text-sm font-semibold text-foreground">Add a note</h3>
      </div>
      <div className="px-4 py-4">
        <textarea
          id={`ticket-note-${ticketId}`}
          value={draftNote}
          onChange={e => setDraftNote(e.target.value)}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          rows={4}
          placeholder="Write a comment…"
          disabled={addNoteMutation.isPending}
          className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm leading-relaxed text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        />
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={
              addNoteMutation.isPending || draftNote.trim().length === 0
            }
            onClick={() => void handleSubmit()}
          >
            {addNoteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding…
              </>
            ) : (
              "Add comment"
            )}
          </Button>
        </div>
      </div>
    </section>
  );
}
